import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Avatar, Button, Card, CoinBadge, EmptyState, Icon, Input, Modal, Skeleton, Tabs, fmtNum } from "../components/ui";
import CharacterCard from "../components/character/Card";
import ImageUploader from "../components/create/ImageUploader";
import PostCard from "../components/social/PostCard";
import PostComposer from "../components/social/PostComposer";
import { loadMe } from "../components/social/useMe";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useI18n } from "../services/i18n";
import content from "../services/api-content";
import "../styles/content.css";

const LINKS = [
  { to: "/coins", icon: "coin", label: "เติมคอยน์ & ประวัติ" },
  { to: "/club", icon: "crown", label: "คลับ VIP" },
  { to: "/rewards", icon: "gift", label: "เช็คอิน / โค้ด / ชวนเพื่อน" },
  { to: "/notifications", icon: "bell", label: "แจ้งเตือน" },
  { to: "/settings", icon: "settings", label: "ตั้งค่า" },
];

function EditProfile({ open, onClose, me, onSaved }) {
  const toast = useToast();
  const { refresh } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (open && me) setF({ displayName: me.displayName || "", username: me.username || "", bio: me.bio || "", photo: me.photoURL ? [me.photoURL] : [], bg: me.backgroundImage ? [me.backgroundImage] : [], links: [...(me.socialLinks || []), "", "", ""].slice(0, 3) });
    setErr("");
  }, [open, me]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    const username = String(f.username || "").trim().toLowerCase();
    if (!f.displayName?.trim()) return setErr("กรุณากรอกชื่อที่แสดง");
    if (username && !/^[a-z0-9_.]{3,20}$/.test(username)) return setErr("ชื่อผู้ใช้ใช้ได้เฉพาะ a-z 0-9 _ . (3-20 ตัว)");
    const links = (f.links || []).map((l) => l.trim()).filter(Boolean);
    if (links.some((l) => !/^https?:\/\//i.test(l))) return setErr("ลิงก์โซเชียลต้องขึ้นต้นด้วย https://");
    setBusy(true);
    setErr("");
    try {
      await content.me.update({ displayName: f.displayName.trim(), ...(username ? { username } : {}), bio: f.bio, photoURL: f.photo[0] || "", backgroundImage: f.bg[0] || "", socialLinks: links });
      await loadMe(true).catch(() => {});
      refresh().catch(() => {});
      toast("บันทึกโปรไฟล์แล้ว", "success");
      onSaved();
      onClose();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="แก้ไขโปรไฟล์" footer={<><Button variant="ghost" onClick={onClose}>ยกเลิก</Button><Button variant="primary" onClick={save} disabled={busy}>{busy ? "กำลังบันทึก..." : "บันทึก"}</Button></>}>
      <div className="strong mb-8">รูปโปรไฟล์</div>
      <ImageUploader value={f.photo || []} onChange={(photo) => set({ photo })} max={1} type="avatar" label="เลือกรูป" />
      <div className="strong mb-8 mt-16">รูปพื้นหลัง</div>
      <ImageUploader value={f.bg || []} onChange={(bg) => set({ bg })} max={1} type="background" label="เลือกรูปพื้นหลัง" />
      <div className="mt-16">
        <Input label="ชื่อที่แสดง (≤20)" maxLength={20} value={f.displayName || ""} onChange={(e) => set({ displayName: e.target.value })} />
        <Input label="ชื่อผู้ใช้ (a-z 0-9 _ . 3-20 ตัว)" maxLength={20} value={f.username || ""} onChange={(e) => set({ username: e.target.value })} />
        <Input multiline label="เกี่ยวกับฉัน (≤500)" maxLength={500} value={f.bio || ""} onChange={(e) => set({ bio: e.target.value })} hint={`${(f.bio || "").length}/500`} />
        {(f.links || []).map((l, i) => <Input key={i} label={`ลิงก์โซเชียล ${i + 1}`} placeholder="https://" value={l} onChange={(e) => set({ links: f.links.map((x, j) => (j === i ? e.target.value : x)) })} />)}
      </div>
      {err ? <div className="error-text" role="alert">{err}</div> : null}
    </Modal>
  );
}

function MyChars({ status }) {
  const { profile } = useAuth();
  const { data, loading, error, reload } = useAsync(() => content.characters.mine(), []);
  if (error) return <EmptyState icon="⚠️" title="โหลดไม่สำเร็จ" action={<Button onClick={reload}>ลองใหม่</Button>}><span className="error-text">{error}</span></EmptyState>;
  if (loading) return <div className="grid-cards">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={260} radius={16} />)}</div>;
  const rows = (data || []).filter((c) => (status === "drafts" ? c.status === "draft" : c.status !== "draft"));
  if (!rows.length) {
    return status === "drafts"
      ? <EmptyState icon="📝" title="ยังไม่มีร่าง" action={<Link className="btn btn--primary" to="/create">สร้างตัวละคร</Link>}>บันทึกร่างระหว่างสร้างตัวละครไว้กลับมาทำต่อได้</EmptyState>
      : <EmptyState icon="🎭" title="ยังไม่มีตัวละคร" action={<Link className="btn btn--primary" to="/create">สร้างตัวละครแรก</Link>}>เริ่มสร้างตัวละครของคุณเอง</EmptyState>;
  }
  return (
    <div className="grid-cards">
      {rows.map((c) => (
        <div key={c.id} className="stack" style={{ gap: 6 }}>
          {status === "drafts" ? <CharacterCard c={{ ...c, status: "draft" }} userName={profile?.displayName} /> : <CharacterCard c={c} userName={profile?.displayName} />}
          <Link className="btn btn--sm" to={`/create?edit=${c.id}`}>{status === "drafts" ? "ทำต่อ" : "แก้ไข"}</Link>
        </div>
      ))}
    </div>
  );
}

function Marked({ type }) {
  const { profile } = useAuth();
  const { data, loading, error } = useAsync(() => content.characters.marked(type), [type]);
  if (error) return <div className="error-text">{error}</div>;
  if (loading) return <div className="grid-cards">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={260} radius={16} />)}</div>;
  if (!data.length) return <EmptyState icon={type === "save" ? "🔖" : "💜"} title={type === "save" ? "ยังไม่ได้บันทึกตัวละคร" : "ยังไม่มีรายการโปรด"}>{type === "save" ? "กดไอคอนบันทึกในหน้าตัวละคร" : "กดหัวใจที่ตัวละครที่ชอบ"}</EmptyState>;
  return <div className="grid-cards">{data.map((c) => <CharacterCard key={c.id} c={c} userName={profile?.displayName} />)}</div>;
}

function MyPosts({ meId }) {
  const { isSuperUser } = useAuth();
  const { data, loading, error, reload } = useAsync(() => (meId ? content.posts.list({ type: "post", userId: meId }) : Promise.resolve({ items: [] })), [meId]);
  return (
    <div className="stack">
      <PostComposer type="post" collapsed onPosted={reload} />
      {error ? <div className="error-text">{error}</div> : loading ? <Skeleton height={100} /> : !data.items.length ? <EmptyState icon="📝" title="ยังไม่มีโพสต์" /> : data.items.map((p) => <PostCard key={p.id} p={p} meId={meId} isSuper={isSuperUser} onDeleted={reload} />)}
    </div>
  );
}

export default function Me() {
  const { profile, coins, isSuperUser } = useAuth();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "mine";
  const [edit, setEdit] = useState(false);
  const me = useAsync(() => loadMe(), []);
  const u = me.data || {};
  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <div className="cover" style={{ background: u.backgroundImage ? `center / cover url(${u.backgroundImage})` : "var(--grad-soft)" }} />
      <div className="profile-head">
        <Avatar name={u.displayName || profile?.displayName || profile?.email} src={u.photoURL} size={92} />
        <div className="grow" style={{ paddingBottom: 6, minWidth: 180 }}><h1>{u.displayName || profile?.displayName || "ผู้ใช้"}</h1><div className="muted clamp-1">@{u.username || profile?.username || "user"} · {profile?.email}</div></div>
        <div className="row" style={{ paddingBottom: 6 }}><CoinBadge amount={coins} /><Button onClick={() => setEdit(true)} disabled={!me.data}>{t("แก้ไขโปรไฟล์")}</Button></div>
      </div>
      {u.bio ? <p className="mt-16" style={{ whiteSpace: "pre-wrap" }}>{u.bio}</p> : null}
      <div className="stats card mt-16">
        <div className="stat"><b>{fmtNum(u.followerCount || 0)}</b><span>{t("ผู้ติดตาม")}</span></div>
        <div className="stat"><b>{fmtNum(u.followingCount || 0)}</b><span>กำลังติดตาม</span></div>
        <div className="stat"><b>{fmtNum(u.characterCount || 0)}</b><span>{t("ตัวละคร")}</span></div>
        <div className="stat"><b>{fmtNum(u.messageCount || 0)}</b><span>{t("ข้อความ")}</span></div>
      </div>
      <div className="stack mt-16">
        {[...LINKS, ...(isSuperUser ? [{ to: "/admin", icon: "shield", label: t("ผู้ดูแลระบบ") }] : [])].map((l) => (
          <Link key={l.to} to={l.to}><Card link className="row"><Icon name={l.icon} /><span className="grow strong">{l.label}</span><Icon name="back" className="faint" style={{ transform: "scaleX(-1)" }} /></Card></Link>
        ))}
      </div>
      <div className="mt-24">
        <Tabs variant="line" value={tab} onChange={(v) => setParams(v === "mine" ? {} : { tab: v }, { replace: true })}
          tabs={[{ value: "mine", label: t("ตัวละครของฉัน") }, { value: "drafts", label: t("ร่าง") }, { value: "liked", label: t("รายการโปรด") }, { value: "saved", label: t("บันทึกไว้") }, { value: "posts", label: t("โพสต์") }]} />
        <div className="mt-16">
          {tab === "mine" || tab === "drafts" ? <MyChars key={tab} status={tab} /> : tab === "liked" ? <Marked type="character" /> : tab === "saved" ? <Marked type="save" /> : <MyPosts meId={u.id} />}
        </div>
      </div>
      <EditProfile open={edit} onClose={() => setEdit(false)} me={me.data} onSaved={me.reload} />
    </div>
  );
}
